import { Injectable } from '@angular/core';
import { NotebookSecretClientService } from '@mode/notebooks/data-access';
import { ToastService } from '@mode/shared/util-toast';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, of, tap, withLatestFrom } from 'rxjs';
import * as NotebookSecretActions from './notebook-secret.actions';
import { NotebookSecretService } from './notebook-secret.service';
import { ConfirmDialog } from '@mode/shared/contract-common';
import { IModalConfig, ISecret, NotebookSecretFacade } from '@mode/notebooks/contract';
import { AccountFacade } from '@mode/accounts/contract';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ReportFacade } from '@mode/reports/domain';

@Injectable({ providedIn: 'root' })
export class NotebookSecretEffects {
  loadSecret$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        NotebookSecretActions.loadSecret,
        NotebookSecretActions.createSecretSuccess,
        NotebookSecretActions.editSecretSuccess,
        NotebookSecretActions.deleteSecretSuccess
      ),
      withLatestFrom(
        this.accountFacade.currentOrg$,
        this.reportFacade.headerReportToken$,
        this.notebookSecretFacade.notebookToken$
      ),
      concatMap(([action, { username }, reportId, notebookId]) =>
        this.notebookSecretClientService.getSecrets(username, reportId, notebookId).pipe(
          map((secrets) => NotebookSecretActions.loadSecretSuccess(secrets)),
          catchError(() => of(NotebookSecretActions.loadSecretFail()))
        )
      )
    )
  );

  createSecret$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotebookSecretActions.createSecret),
      withLatestFrom(
        this.accountFacade.currentOrg$,
        this.reportFacade.headerReportToken$,
        this.notebookSecretFacade.notebookToken$,
        this.notebookSecretFacade.notebookSession$
      ),
      concatMap(([{ payload, ...action }, { username }, reportId, notebookId, notebookSession]) => {
        const { active, token } = notebookSession || {};
        return this.notebookSecretClientService
          .createSecret(username, reportId, notebookId, {
            ...payload,
            ...(active && { session_id: token }),
          })
          .pipe(
            map(() => NotebookSecretActions.createSecretSuccess()),
            catchError(() => of(NotebookSecretActions.createSecretFail()))
          );
      })
    )
  );

  createSecretSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotebookSecretActions.createSecretSuccess),
        tap(() => this.toastService.success('Secret created'))
      ),
    { dispatch: false }
  );

  createSecretError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotebookSecretActions.createSecretFail),
        tap(() => this.toastService.error('Secret creation failed. Please try again'))
      ),
    { dispatch: false }
  );

  editSecret$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotebookSecretActions.editSecret),
      withLatestFrom(
        this.accountFacade.currentOrg$,
        this.reportFacade.headerReportToken$,
        this.notebookSecretFacade.notebookToken$,
        this.notebookSecretFacade.notebookSession$
      ),
      concatMap(([{ secretId, payload, ...action }, { username }, reportId, notebookId, notebookSession]) => {
        const { active, token } = notebookSession || {};
        return this.notebookSecretClientService
          .editSecret(username, reportId, notebookId, secretId, {
            ...payload,
            ...(active && { session_id: token }),
          })
          .pipe(
            map(() => NotebookSecretActions.editSecretSuccess()),
            catchError(() => of(NotebookSecretActions.editSecretFail()))
          );
      })
    )
  );

  editSecretSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotebookSecretActions.editSecretSuccess),
        tap(() => this.toastService.success('Secret updated'))
      ),
    { dispatch: false }
  );

  editSecretError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotebookSecretActions.editSecretFail),
        tap(() => this.toastService.error('Failed to update secret. Please try again'))
      ),
    { dispatch: false }
  );

  deleteSecret$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotebookSecretActions.deleteSecret),
      withLatestFrom(
        this.accountFacade.currentOrg$,
        this.reportFacade.headerReportToken$,
        this.notebookSecretFacade.notebookToken$,
        this.notebookSecretFacade.notebookSession$
      ),
      concatMap(([{ secretId, ...action }, { username }, reportId, notebookId, notebookSession]) => {
        const { active, token } = notebookSession || {};
        return this.notebookSecretClientService
          .deleteSecret(username, reportId, notebookId, secretId, {
            ...(active && { session_id: token }),
          })
          .pipe(
            map(() => NotebookSecretActions.deleteSecretSuccess()),
            catchError(() => of(NotebookSecretActions.deleteSecretFail()))
          );
      })
    )
  );

  deleteSecretSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotebookSecretActions.deleteSecretSuccess),
        tap(() => this.toastService.success('Secret deleted'))
      ),
    { dispatch: false }
  );

  deleteSecretError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotebookSecretActions.deleteSecretFail),
        tap(() => this.toastService.error('Failed to delete secret. Please try again'))
      ),
    { dispatch: false }
  );

  openDeleteSecretModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotebookSecretActions.openDeleteSecretModal),
        map(({ secret, modalConfig, cb }) => this.openDialog(secret, modalConfig, cb))
      ),
    { dispatch: false }
  );

  openDuplicateDialogFromSecret$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotebookSecretActions.openDuplicateDialogFromSecret),
        map(({ cb }) => this.openDuplicateDialogForSecret(cb))
      ),
    { dispatch: false }
  );

  public openDialog(secret: ISecret, modalConfig: IModalConfig, cb: () => void) {
    const { dialogTitle, dialogText, confirmText, cancelText } = modalConfig;
    const dialogRef = this.confirmDialog.open({
      dialogTitle,
      dialogText,
      confirmText,
      cancelText,
      confirmButtonColor: 'alert',
      confirmButtonType: 'secondary',
      confirmAction: () => this.notebookSecretService.deleteSecret(secret.token),
    });
    dialogRef.subscribe(() => cb());
  }

  public openDuplicateDialogForSecret(cb: () => void) {
    this.confirmDialog.open({
      dialogTitle: 'Report secrets won’t be duplicated',
      dialogText: `<p>This Report contains <b class="bold-text unset-margin">one or more Notebook secrets</b> that won’t be duplicated. This can cause breaking changes to the Notebook unless they’re manually re-created.</p>`,
      confirmText: 'OK',
      cancelText: 'Cancel',
      confirmButtonColor: 'alert',
      confirmButtonType: 'primary',
      confirmAction: () => cb(),
    });
  }

  constructor(
    private actions$: Actions,
    private notebookSecretClientService: NotebookSecretClientService,
    private toastService: ToastService,
    private notebookSecretService: NotebookSecretService,
    private notebookSecretFacade: NotebookSecretFacade,
    private confirmDialog: ConfirmDialog,
    private accountFacade: AccountFacade,
    private reportFacade: ReportFacade
  ) {}
}
